/**
 * Iter 253z-11 · Performance Marketing · Reports page.
 *
 * On-demand CSV export + scheduled reports (daily/weekly/monthly) that
 * pipe to workspace emails via the digest scheduler.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, CalendarClock, Trash2, Plus, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function PerformanceReports() {
  const { activeWorkspaceId } = useAuth();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [downloading, setDownloading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [busy, setBusy] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", cadence: "weekly", format: "csv", recipients: "", brand_id: "" });

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [b, s] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/brands`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/perf/report-schedules`),
      ]);
      setBrands(b.data || []);
      setSchedules(s.data || []);
    } catch (e) { toast.error("Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/workspaces/${activeWorkspaceId}/perf/reports/export`, {
        params: { date_from: dateFrom, date_to: dateTo, brand_id: brandId || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `perf-${dateFrom}_${dateTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (e) { toast.error("Export failed"); }
    finally { setDownloading(false); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/workspaces/${activeWorkspaceId}/perf/reports/export-pdf`, {
        params: { date_from: dateFrom, date_to: dateTo, brand_id: brandId || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `perf-${dateFrom}_${dateTo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF report ready");
    } catch (e) { toast.error("PDF export failed"); }
    finally { setPdfBusy(false); }
  };

  const createSchedule = async () => {
    if (!form.name.trim()) { toast.error("Give the schedule a name"); return; }
    const recips = form.recipients.split(",").map((r) => r.trim()).filter(Boolean);
    setCreating(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/perf/report-schedules`, {
        name: form.name.trim(),
        brand_id: form.brand_id || null,
        cadence: form.cadence,
        format: form.format,
        recipients: recips,
      });
      toast.success("Schedule created");
      setForm({ name: "", cadence: "weekly", format: "csv", recipients: "", brand_id: "" });
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setCreating(false); }
  };

  const removeSchedule = async (s) => {
    if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
    try {
      await api.delete(`/perf/report-schedules/${s.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Performance Marketing"
        title="Reports"
        description="Download performance CSVs on demand, or schedule automated recurring reports via email."
        testid="perf-reports-header"
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-6">
        {/* One-off export */}
        <section className="border border-border p-5 space-y-3" data-testid="perf-export-card">
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4 text-primary" />
            <div className="font-bold text-sm">One-off CSV export</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Brand</Label>
              <Select value={brandId || "_all"} onValueChange={(v) => setBrandId(v === "_all" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="perf-export-brand"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All brands</SelectItem>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-xs" data-testid="perf-export-from" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-xs" data-testid="perf-export-to" />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={download} disabled={downloading} className="rounded-none flex-1" data-testid="perf-export-download">
                {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                {downloading ? "CSV…" : "CSV"}
              </Button>
              <Button onClick={downloadPdf} disabled={pdfBusy} variant="outline" className="rounded-none flex-1" data-testid="perf-export-pdf">
                {pdfBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                {pdfBusy ? "PDF…" : "PDF"}
              </Button>
            </div>
          </div>
        </section>

        {/* Scheduled reports */}
        <section className="border border-border" data-testid="perf-schedules-card">
          <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-primary" />
            <div className="font-bold text-sm">Scheduled reports</div>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
              <Input placeholder="Schedule name (e.g. Weekly Meta digest)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 text-xs sm:col-span-2" data-testid="perf-sched-name" />
              <Select value={form.brand_id || "_all"} onValueChange={(v) => setForm({ ...form, brand_id: v === "_all" ? "" : v })}>
                <SelectTrigger className="h-9 text-xs" data-testid="perf-sched-brand"><SelectValue placeholder="Brand" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All brands</SelectItem>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={form.cadence} onValueChange={(v) => setForm({ ...form, cadence: v })}>
                <SelectTrigger className="h-9 text-xs" data-testid="perf-sched-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                <SelectTrigger className="h-9 text-xs" data-testid="perf-sched-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="pdf">PDF (branded)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="label-eyebrow mb-1 inline-block">Email recipients (comma-separated)</Label>
                <Input placeholder="cmo@brand.com, growth@agency.com" value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} className="h-9 text-xs" data-testid="perf-sched-recipients" />
              </div>
              <Button onClick={createSchedule} disabled={creating} className="rounded-none" data-testid="perf-sched-create">
                {creating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                Schedule
              </Button>
            </div>

            {busy ? <div className="text-xs text-muted-foreground">Loading…</div>
              : schedules.length === 0 ? <div className="text-xs text-muted-foreground border border-dashed border-border p-4 text-center">No scheduled reports yet.</div>
              : (
                <table className="w-full text-xs border border-border">
                  <thead className="bg-muted/20 text-[10px] uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Cadence</th>
                      <th className="text-left px-3 py-2">Format</th>
                      <th className="text-left px-3 py-2">Recipients</th>
                      <th className="text-left px-3 py-2">Next run</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {schedules.map((s) => (
                      <tr key={s.id} data-testid={`perf-sched-row-${s.id}`}>
                        <td className="px-3 py-2 font-medium">{s.name}</td>
                        <td className="px-3 py-2">{s.cadence}</td>
                        <td className="px-3 py-2 uppercase">{s.format}</td>
                        <td className="px-3 py-2 text-[10px] text-muted-foreground truncate max-w-xs">{(s.recipients || []).join(", ") || "—"}</td>
                        <td className="px-3 py-2 tabular-nums text-[10px]">{s.next_run_at?.slice(0, 16).replace("T", " ")}</td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="ghost" className="rounded-none h-7 w-7 text-rose-600" onClick={() => removeSchedule(s)} data-testid={`perf-sched-del-${s.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }
          </div>
        </section>
      </div>
    </>
  );
}
